/**
 * Sort modes of the two queue views («Команды», «Исполнители»).
 *
 * The default is «Вручную» — the tie-free order the owner arranges by dragging,
 * stored as `teamPosition` / `personPosition` (the stream's F-25 twin). The
 * other modes keep the rank ordering they always had: `teamPriority` stays the
 * coarse, filter-independent field it is — manual order is layered ON TOP of it
 * and simply ignores it in manual mode.
 */

import type { Person, Project } from '../api/types';
import { compareDueDate, sortPersonQueue, sortTeamQueue } from './capacity';
import { sortPersonQueueManual, sortTeamQueueManual } from './manual-order';

export type QueueSortKey = 'manual' | 'priority' | 'dueDate' | 'title';

export const QUEUE_SORT_LABELS: Record<QueueSortKey, string> = {
  manual: 'Вручную',
  priority: 'приоритет',
  dueDate: 'срок',
  title: 'название',
};

export const QUEUE_SORT_KEYS = Object.keys(QUEUE_SORT_LABELS) as QueueSortKey[];

export const DEFAULT_QUEUE_SORT: QueueSortKey = 'manual';

/** A valid sort key from a stored value (per-view state falls back to the default). */
export function asQueueSortKey(value: string | undefined): QueueSortKey {
  return QUEUE_SORT_KEYS.includes(value as QueueSortKey) ? (value as QueueSortKey) : DEFAULT_QUEUE_SORT;
}

const byTitle = (a: Project, b: Project): number => a.title.localeCompare(b.title, 'ru');

/**
 * The team queue in the chosen mode. Non-manual modes keep the F-19b ranking
 * (best teamPriority first) and only re-sort its ties; «Вручную» ignores the
 * ranking completely and follows `teamPosition`.
 */
export function sortTeamQueueWith(
  teamId: string,
  projects: readonly Project[],
  people: readonly Person[],
  key: QueueSortKey = DEFAULT_QUEUE_SORT,
): Project[] {
  if (key === 'manual') return sortTeamQueueManual(teamId, projects, people);
  const ranked = sortTeamQueue(teamId, projects, people);
  if (key === 'priority') return ranked;
  if (key === 'dueDate') return [...ranked].sort((a, b) => compareDueDate(a.dueDate, b.dueDate) || byTitle(a, b));
  return [...ranked].sort(byTitle);
}

/** The person's queue in the chosen mode (same contract, `personPriority` ranks). */
export function sortPersonQueueWith(
  personId: string,
  projects: readonly Project[],
  key: QueueSortKey = DEFAULT_QUEUE_SORT,
): Project[] {
  if (key === 'manual') return sortPersonQueueManual(personId, projects);
  const ranked = sortPersonQueue(personId, projects);
  if (key === 'priority') return ranked;
  if (key === 'dueDate') return [...ranked].sort((a, b) => compareDueDate(a.dueDate, b.dueDate) || byTitle(a, b));
  return [...ranked].sort(byTitle);
}
